import { useEffect, useMemo, useState } from "react"
import { Link, useNavigate, useParams } from "react-router"
import { motion, AnimatePresence } from "motion/react"
import { toast } from "sonner"
import { photoLinksOf } from "@/lib/estimates/photo-links"
import { componentLabel } from "@/lib/estimates/catalog"
import {
  ArrowLeft,
  XClose,
  AlertTriangle,
  Loading01,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  CheckCircle,
  ClockSnooze,
  DotsHorizontal,
  Edit03,
  Download01,
  PlayCircle,
  XCircle,
  Phone01,
  Zap,
  CalendarDate,
} from "@untitledui/icons"

import {
  estimateCustomerName,
  estimateCustomerEmail,
  estimateCustomerPhone,
  vehicleDisplayName,
  type ApiEstimate,
  type ApiEstimateLineItem,
  type ApiEstimateImage,
  type ApiOperation,
  type EstimateStatus,
  type SalesLeadDepartment,
  type SectionWorkType,
  type VehicleSection,
} from "@/lib/api/types"
import {
  useEstimate,
  useUpdateEstimateStatus,
  useUpdateEstimateCustomer,
  useMarkAsTotalLoss,
  useUnmarkAsTotalLoss,
  useSetDeferral,
} from "@/lib/api/use-estimates"
import { estimatesApi, estimateDeferral } from "@/lib/api/estimates"
import {
  readIntakeMeta,
  type IntakeBestTime,
  type IntakeCause,
  type IntakeContactChannel,
  type IntakeDrivable,
  type IntakeInsuranceMode,
} from "@/lib/api/public-consumer"
import { SURFACE_LABEL } from "@/lib/intake/intake-config"
import { StatusBadge } from "@/components/estimates/status-badge"
import { PrimaryButton } from "@/components/onboarding/onboarding-shell"
import { DesignMenu } from "@/components/ui/design-menu"
import { ProtectedRoute } from "@/lib/auth/protected-route"
import { ALL_ROLES, Roles } from "@/lib/team/roles"
import { AppLayout } from "@/components/layout/app-layout"
import { estimateVerdict } from "@/lib/estimates/estimate-verdict"
import { useCustomerActivity, useSetCustomerConsent } from "@/lib/api/use-customer-activity"
import { useEstimateTasks } from "@/lib/api/use-tasks"
import { DECLINE_LABEL, OUTCOME_LABEL, attemptLabel, type TaskDoc } from "@/lib/api/tasks"
import { CustomerConversation } from "@/components/customers/customer-conversation"
import { useAuth } from "@/lib/auth/auth-context"
import { useCurrentRole } from "@/lib/team/use-current-role"
import { useOrganization } from "@/lib/organization/organization-context"
import { cx } from "@/lib/utils/cx"

/* ──────────────────────────────────────────────────────────
 * Estimate detail — full-page record at /estimates/:id.
 *
 * Layout matches CCC document language: header card → info
 * cards → photos → damage table → operations → notes → total
 * band. PDF download goes through the BE export endpoint
 * (estimatesApi.exportPdf), so this page has no print styles
 * — `Download as PDF` always hits the API.
 * ────────────────────────────────────────────────────────── */

function EstimateDetailPageInner() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const estimateId = id ? Number(id) : null

  const { data: estimate, isLoading, error } = useEstimate(estimateId, {
    refetchInterval: (query) => {
      const data = query.state.data as ApiEstimate | undefined
      return data?.status === "Loading" ? 4000 : false
    },
  })

  if (isLoading) {
    return (
      <Page>
        <Skeleton />
      </Page>
    )
  }
  if (error || !estimate) {
    return (
      <Page>
        <NotFound onBack={() => navigate("/estimates")} />
      </Page>
    )
  }

  return (
    <Page>
      <Inner estimate={estimate} />
    </Page>
  )
}

function Page({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-zinc-50/40">
      <div className="mx-auto max-w-5xl px-4 md:px-8 py-6 md:py-10">{children}</div>
    </div>
  )
}

/* ──────────────────────────────────────────────────────────
 * Inner — composed once the estimate is loaded
 * ────────────────────────────────────────────────────────── */

function Inner({ estimate }: { estimate: ApiEstimate }) {
  const navigate = useNavigate()
  // li.manHours is already the sum of its child operations' laborHours
  // (orchestrator stamps it that way), so adding manHours alone gives
  // the correct labour total without double-counting.
  const totalHours = useMemo(
    () =>
      (estimate.lineItems ?? []).reduce(
        (sum, li) => sum + (li.manHours || 0),
        0,
      ),
    [estimate.lineItems],
  )
  const totalOps = useMemo(
    () =>
      (estimate.lineItems ?? []).reduce(
        (sum, li) => sum + (li.operations?.length ?? 0),
        0,
      ),
    [estimate.lineItems],
  )

  return (
    <>
      <BackLink />

      <Header
        estimate={estimate}
        onEdit={() => navigate(`/estimates/${estimate.id}/edit`)}
      />

      <SectionStack>
        <AppointmentBlock estimate={estimate} />
        <InfoGrid estimate={estimate} />
        <FollowUpLens estimate={estimate} />
        {estimate.images && estimate.images.length > 0 && (
          <PhotosSection images={estimate.images} items={estimate.lineItems ?? []} />
        )}
        <DamageSection items={estimate.lineItems ?? []} />
        {totalOps > 0 && (
          <OperationsSection
            lineItems={estimate.lineItems ?? []}
            totalHours={totalHours}
            totalOps={totalOps}
          />
        )}
        <FollowUpTimeline estimate={estimate} />
        <TotalBand estimate={estimate} totalHours={totalHours} />
      </SectionStack>
    </>
  )
}

function SectionStack({ children }: { children: React.ReactNode }) {
  return <div className="mt-6 space-y-10">{children}</div>
}

// Per-estimate lens into the customer's follow-up — the same conversation as
// the Customer Card, filtered to this estimate, with a jump to the full card.
// Only surfaces once follow-up is actually live — i.e. once the estimate has
// entered InProgress, which is the act that contacts the customer. Confirmed
// is deliberately absent: nothing has been said to anyone yet, so a
// conversation panel there would promise a thread that doesn't exist.
const FOLLOWUP_LIVE = ["InProgress", "Scheduled", "Completed"]
// When the customer's booked, the estimate says so — date, time, and the way
// to the calendar (Phase 4 connective tissue).
function AppointmentBlock({ estimate }: { estimate: ApiEstimate }) {
  const { activeTeamKind } = useOrganization()
  if (estimate.status !== "Scheduled" || !estimate.scheduledAt) return null
  const dt = new Date(estimate.scheduledAt)
  const ymd = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(
    dt.getDate(),
  ).padStart(2, "0")}`
  return (
    <section className="rounded-2xl border border-sky-200/70 bg-sky-50/50 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
      <p className="flex items-center gap-2 text-[13px] font-semibold text-sky-900">
        <CalendarDate className="size-4 text-sky-600 shrink-0" aria-hidden />
        Booked —{" "}
        {dt.toLocaleString("en-US", {
          weekday: "long",
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        })}
      </p>
      {activeTeamKind === "bodyshop" && (
        <Link
          to={`/calendar?d=${ymd}`}
          className="text-[12.5px] font-semibold text-sky-700 hover:text-sky-800 shrink-0"
        >
          View on calendar →
        </Link>
      )}
    </section>
  )
}

function FollowUpLens({ estimate }: { estimate: ApiEstimate }) {
  const customerId = estimate.vehicle?.customer?.id ?? null
  const live = FOLLOWUP_LIVE.includes(estimate.status)
  const { data } = useCustomerActivity(live ? customerId : null)
  const setConsent = useSetCustomerConsent(customerId ?? 0)
  if (customerId == null || !live) return null
  const stopped = data ? data.consent !== "subscribed" : false
  const optedOut = data?.consent === "opted_out"
  return (
    <section>
      <div className="flex items-center justify-between gap-3 mb-2 px-1">
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-400">
          Automated follow-up
        </p>
        <div className="flex items-center gap-3 shrink-0">
          {/* Stop the drip right here when the advisor handled it by phone —
              no need to open the full card (F23). Opt-out is customer-set, not
              reversible from here. */}
          {data &&
            !optedOut &&
            (stopped ? (
              <button
                type="button"
                disabled={setConsent.isPending}
                onClick={() => setConsent.mutate({ state: "subscribed" })}
                className="text-[12px] font-semibold text-amber-700 hover:text-amber-800 transition-colors disabled:opacity-50"
              >
                Resume follow-up
              </button>
            ) : (
              <button
                type="button"
                disabled={setConsent.isPending}
                onClick={() => setConsent.mutate({ state: "handled_offline" })}
                className="text-[12px] font-semibold text-zinc-500 hover:text-zinc-800 transition-colors disabled:opacity-50"
              >
                Handled offline — stop
              </button>
            ))}
          <Link
            to={`/customers/${customerId}`}
            className="text-[12px] font-semibold text-emerald-700 hover:text-emerald-800 transition-colors"
          >
            Full customer →
          </Link>
        </div>
      </div>
      {data ? (
        <CustomerConversation
          customerId={customerId}
          events={data.events}
          consent={data.consent}
          estimateId={estimate.id}
          jobCompleted={estimate.status === "Completed"}
          customerName={estimate.vehicle?.customer?.name}
        />
      ) : (
        <div className="rounded-2xl border border-zinc-200/80 bg-white p-6 text-center text-[13px] text-zinc-400">
          Loading follow-up…
        </div>
      )}
    </section>
  )
}

/* ──────────────────────────────────────────────────────────
 * Back link + Header card
 * ────────────────────────────────────────────────────────── */

function BackLink() {
  return (
    <Link
      to="/estimates"
      className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-zinc-500 hover:text-zinc-900 transition-colors mb-4"
    >
      <ArrowLeft className="size-3.5" aria-hidden />
      Estimates
    </Link>
  )
}

function Header({
  estimate,
  onEdit,
}: {
  estimate: ApiEstimate
  onEdit: () => void
}) {
  const customerName = estimateCustomerName(estimate)
  const vehicleName = vehicleDisplayName(estimate.vehicle)
  const deferral = estimateDeferral(estimate)
  // "AI is analysing" belongs to the Loading state only. InProgress now means
  // outreach/autopilot is running, not AI work — it gets its own calm note (F42).
  const isAnalysing = estimate.status === "Loading"
  const isAutopilot = estimate.status === "InProgress"
  // Status writes are a body-shop action; dealerships see the badge but no
  // dropdown. Mirrors the pipeline-board move-by-drag gate in estimates.tsx.
  const { activeTeamKind } = useOrganization()
  const canChangeStatus = activeTeamKind === "bodyshop"

  return (
    <motion.header
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
      className="rounded-2xl border border-zinc-200/80 bg-white shadow-sm overflow-hidden"
    >
      {isAnalysing && (
        <div
          aria-hidden
          className="h-px bg-gradient-to-r from-transparent via-emerald-500 to-transparent animate-pulse"
        />
      )}
      <div className="px-6 md:px-8 py-6 md:py-7">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap text-[12px]">
              <span className="font-semibold text-zinc-400 tabular-nums">
                #{estimate.id}
              </span>
              <span className="text-zinc-300">·</span>
              <StatusBadge status={estimate.status} size="sm" />
              {deferral && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200"
                  title={deferral.note}
                >
                  <ClockSnooze className="size-3" aria-hidden />
                  Deferred
                </span>
              )}
              {estimate.totalLoss && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-red-50 text-red-700 ring-1 ring-inset ring-red-200">
                  <AlertTriangle className="size-3" aria-hidden />
                  Total loss
                </span>
              )}
            </div>
            <h1 className="mt-2 text-[26px] md:text-[30px] font-bold text-zinc-900 tracking-tight leading-tight">
              {customerName}
            </h1>
            <p className="mt-0.5 text-[14px] text-zinc-500">{vehicleName}</p>
            {estimate.createdBy && (
              <p className="mt-1 text-[12.5px] text-zinc-400">
                Rider: {estimate.createdBy.firstName} {estimate.createdBy.lastName}
                {" · "}
                {new Date(estimate.createdAt).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </p>
            )}
            {deferral?.note && (
              <p className="mt-1 text-[12.5px] text-amber-700">
                Deferred — {deferral.note}
              </p>
            )}
            {canChangeStatus && estimate.status === "Pending" && (
              <VerdictNote estimate={estimate} />
            )}
            {canChangeStatus &&
              (estimate.status === "Pending" || estimate.status === "Confirmed") &&
              !estimate.vehicle?.customer?.phone && (
                <PhoneQuickAdd estimate={estimate} />
              )}
          </div>
          <div className="shrink-0 flex items-center gap-2">
            {canChangeStatus && estimate.status === "Pending" && (
              <ConfirmCTA estimate={estimate} />
            )}
            <PrimaryButton onClick={onEdit} type="button" variant="dark">
              <span className="inline-flex items-center gap-2">
                <Edit03 className="size-4" aria-hidden />
                Edit
              </span>
            </PrimaryButton>
            {canChangeStatus && <StatusMenu estimate={estimate} />}
            <OverflowMenu estimate={estimate} />
          </div>
        </div>

        {isAnalysing && (
          <p className="mt-4 inline-flex items-center gap-2 text-[12px] text-zinc-500">
            <Loading01 className="size-3.5 animate-spin text-emerald-600" aria-hidden />
            AI is analysing — this view refreshes automatically.
          </p>
        )}
        {isAutopilot && (
          <p className="mt-4 inline-flex items-center gap-2 text-[12px] text-zinc-500">
            <Zap className="size-3.5 text-emerald-600" aria-hidden />
            On autopilot — automated follow-up is running. Nothing needed from you.
          </p>
        )}
      </div>
    </motion.header>
  )
}

// Confirm — the one human action, made effortless. The estimate arrives
// pre-judged (looks good vs. worth a look). Confirming says "I read it and it
// makes sense" and NOTHING leaves the building: it parks the estimate in the
// review lane so a shop can clear a morning's batch in one pass and make the
// calls afterwards. Contacting the customer is the separate, deliberate move
// into In Progress.
function ConfirmCTA({ estimate }: { estimate: ApiEstimate }) {
  const update = useUpdateEstimateStatus()
  const verdict = estimateVerdict(estimate)
  const review = verdict.level === "review"
  const hasPhone = !!estimate.vehicle?.customer?.phone
  return (
    <button
      type="button"
      disabled={update.isPending}
      onClick={() =>
        update.mutate(
          { estimateId: estimate.id, status: "Confirmed" },
          {
            onSuccess: () =>
              // Confirm releases contact to a real customer — the one outward-
              // facing tap. Give it a take-back window: Undo reverts to Pending
              // before the first text goes out (#3).
              toast.success(
                hasPhone
                  ? "Confirmed — ready to call. Nothing sent yet."
                  : "Confirmed — ready to call. Add a number before starting follow-up.",
                {
                  action: {
                    label: "Undo",
                    onClick: () =>
                      update.mutate(
                        { estimateId: estimate.id, status: "Pending" },
                        { onSuccess: () => toast("Confirm undone — back to pending") },
                      ),
                  },
                },
              ),
            onError: () => toast.error("Couldn't confirm the estimate."),
          },
        )
      }
      title={review ? `Worth a look — ${verdict.reasons.join(" · ")}` : "Looks good"}
      className={cx(
        "inline-flex items-center gap-2 h-12 px-4 rounded-xl text-[13.5px] font-semibold text-white transition-colors disabled:opacity-50",
        review ? "bg-amber-500 hover:bg-amber-600" : "bg-teal-600 hover:bg-teal-700",
      )}
    >
      <CheckCircle className="size-4" aria-hidden />
      {review ? "Review & confirm" : "Confirm estimate"}
    </button>
  )
}

// Inline phone quick-add — a no-phone estimate is a dead end for text follow-up,
// so instead of just flagging it we let the advisor fix it in one field, right
// where they confirm (#11). MOCK-ONLY write (real BE has no customer update).
function PhoneQuickAdd({ estimate }: { estimate: ApiEstimate }) {
  const update = useUpdateEstimateCustomer()
  const [open, setOpen] = useState(false)
  const [phone, setPhone] = useState("")
  const close = () => {
    setOpen(false)
    setPhone("")
  }
  const save = () => {
    const p = phone.trim()
    if (!p || update.isPending) return
    update.mutate(
      { estimateId: estimate.id, phone: p },
      {
        onSuccess: () => {
          close()
          toast.success("Phone added — text follow-up can run now.")
        },
        onError: () => toast.error("Couldn't save the number."),
      },
    )
  }
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-2 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-amber-700 hover:text-amber-800 transition-colors"
      >
        <Phone01 className="size-3.5" aria-hidden />
        Add phone to start follow-up
      </button>
    )
  }
  return (
    <div className="mt-2 flex items-center gap-2">
      <input
        type="tel"
        autoFocus
        aria-label="Customer phone number"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") save()
          if (e.key === "Escape") close()
        }}
        placeholder="(555) 123-4567"
        className="h-9 w-44 rounded-lg border border-zinc-200 bg-white px-3 text-[13px] text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-300"
      />
      <button
        type="button"
        onClick={save}
        disabled={!phone.trim() || update.isPending}
        className="h-9 px-3 rounded-lg bg-zinc-900 text-white text-[12.5px] font-semibold hover:bg-black transition-colors disabled:opacity-40"
      >
        Save
      </button>
      <button
        type="button"
        onClick={close}
        className="h-9 px-2 text-[12.5px] font-medium text-zinc-500 hover:text-zinc-700"
      >
        Cancel
      </button>
    </div>
  )
}

// The pre-check verdict, rendered as visible text (not a hover-only tooltip)
// so it reads on touch — the service lane's primary device.
function VerdictNote({ estimate }: { estimate: ApiEstimate }) {
  const v = estimateVerdict(estimate)
  if (v.level === "clean") {
    return (
      <p className="mt-2 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-emerald-700">
        <CheckCircle className="size-3.5" aria-hidden /> Looks good — ready to confirm
      </p>
    )
  }
  return (
    <div className="mt-2 flex items-start gap-1.5 text-[12.5px] font-medium text-amber-700">
      <AlertTriangle className="size-3.5 mt-0.5 shrink-0" aria-hidden />
      <span>Worth a look — {v.reasons.join(" · ")}</span>
    </div>
  )
}

/* ──────────────────────────────────────────────────────────
 * Status menu — pick the destination, not a linear next-state.
 * ────────────────────────────────────────────────────────── */

// Human-owned statuses only. InProgress / Scheduled / Completed advance on
// their own (outreach send → booking → RO close), matching the board's
// drag-rejection — so they're not offered here as manual moves.
const USER_STATUSES: Exclude<EstimateStatus, "Loading">[] = [
  "Draft",
  "Pending",
  "Confirmed",
  "Declined",
]

const STATUS_LABEL: Record<EstimateStatus, string> = {
  Draft: "Draft",
  Loading: "Loading",
  Pending: "Pending",
  Confirmed: "Confirmed",
  Scheduled: "Scheduled",
  InProgress: "In progress",
  Completed: "Completed",
  Declined: "Declined",
}

function StatusMenu({ estimate }: { estimate: ApiEstimate }) {
  const update = useUpdateEstimateStatus()
  const change = (next: Exclude<EstimateStatus, "Loading">) => {
    if (next === estimate.status) return
    update.mutate(
      { estimateId: estimate.id, status: next },
      {
        onSuccess: () => toast.success(`Status → ${STATUS_LABEL[next]}`),
        onError: () => toast.error("Couldn't update status."),
      },
    )
  }

  return (
    <DesignMenu.Root>
      <DesignMenu.Trigger asChild>
        <button
          type="button"
          className={cx(
            "inline-flex items-center gap-1.5 h-12 px-3.5 rounded-xl",
            "border border-zinc-200 bg-white hover:bg-zinc-50 transition-colors",
            "text-zinc-700 group",
          )}
          aria-label="Change status"
        >
          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-400">
            Status
          </span>
          <span className="text-[13.5px] font-semibold text-zinc-900">
            {STATUS_LABEL[estimate.status]}
          </span>
          <ChevronDown
            className="size-3.5 text-zinc-400 transition-transform group-data-[state=open]:rotate-180"
            aria-hidden
          />
        </button>
      </DesignMenu.Trigger>
      <DesignMenu.Content align="end">
        <DesignMenu.Label>Change status</DesignMenu.Label>
        {USER_STATUSES.map((s) => {
          const isCurrent = s === estimate.status
          return (
            <DesignMenu.Item
              key={s}
              destructive={s === "Declined"}
              disabled={isCurrent || update.isPending}
              onSelect={() => change(s)}
              icon={
                isCurrent ? (
                  <CheckCircle className="text-emerald-600" />
                ) : (
                  <span className={cx("size-2 rounded-full", statusDotClass(s))} />
                )
              }
            >
              {STATUS_LABEL[s]}
            </DesignMenu.Item>
          )
        })}
      </DesignMenu.Content>
    </DesignMenu.Root>
  )
}

function statusDotClass(s: EstimateStatus): string {
  switch (s) {
    case "Draft":
      return "bg-zinc-400"
    case "Pending":
      return "bg-amber-400"
    case "Confirmed":
      return "bg-teal-400"
    case "Scheduled":
      return "bg-sky-400"
    case "InProgress":
      return "bg-blue-400"
    case "Completed":
      return "bg-emerald-500"
    case "Declined":
      return "bg-red-400"
    case "Loading":
      return "bg-orange-400"
  }
}

/* ──────────────────────────────────────────────────────────
 * Overflow menu — Download as PDF (API), Mark/Unmark Total Loss
 * (role-gated to bodyshop Estimator/GM, mirrors real BE
 * RolesGuard on POST/DELETE /v1/estimates/:id/total-loss),
 * deferral actions (req 2.4.x — manager-and-up + admins only;
 * SAs don't clear opportunities).
 * ────────────────────────────────────────────────────────── */

// Statuses where an estimate is still a live opportunity — the only ones
// where parking it as "deferred" means anything.
const DEFERRABLE_STATUSES: EstimateStatus[] = ["Pending", "InProgress", "Scheduled"]

function OverflowMenu({ estimate }: { estimate: ApiEstimate }) {
  const [downloading, setDownloading] = useState(false)
  const [markDialogOpen, setMarkDialogOpen] = useState(false)
  const [deferDialogOpen, setDeferDialogOpen] = useState(false)
  const [confirmDeadOpen, setConfirmDeadOpen] = useState(false)
  const { user } = useAuth()
  const role = useCurrentRole()
  const { activeTeamKind } = useOrganization()
  const canMarkTotalLoss =
    activeTeamKind === "bodyshop" &&
    (role === Roles.Estimator || role === Roles.GeneralManager)
  const canManageDeferral =
    !!user?.admin ||
    role === Roles.Director ||
    role === Roles.GeneralManager ||
    role === Roles.ServiceManager

  const deferral = estimateDeferral(estimate)
  const showDefer =
    canManageDeferral && !deferral && DEFERRABLE_STATUSES.includes(estimate.status)
  const showDeferred = canManageDeferral && !!deferral

  const markMutation = useMarkAsTotalLoss()
  const unmarkMutation = useUnmarkAsTotalLoss()
  const deferralMutation = useSetDeferral()

  const handleReactivate = () => {
    deferralMutation.mutate(
      { estimateId: estimate.id, action: "reactivate" },
      {
        onSuccess: () => toast.success("Estimate re-activated"),
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : "Couldn't re-activate"),
      },
    )
  }

  const handleDownload = async () => {
    if (downloading) return
    setDownloading(true)
    try {
      const { blob, filename } = await estimatesApi.exportPdf(estimate.id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = filename ?? `estimate-${estimate.id}.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't download PDF")
    } finally {
      setDownloading(false)
    }
  }

  const handleUnmark = () => {
    unmarkMutation.mutate(estimate.id, {
      onSuccess: () => toast.success("Total loss cleared"),
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : "Couldn't clear total loss"),
    })
  }

  return (
    <>
      <DesignMenu.Root>
        <DesignMenu.Trigger asChild>
          <button
            type="button"
            className={cx(
              "inline-flex items-center justify-center size-12 rounded-xl",
              "border border-zinc-200 bg-white hover:bg-zinc-50",
              "text-zinc-600 hover:text-zinc-900 transition-colors",
            )}
            aria-label="More actions"
          >
            <DotsHorizontal className="size-5" aria-hidden />
          </button>
        </DesignMenu.Trigger>
        <DesignMenu.Content align="end">
          <DesignMenu.Item
            icon={<Download01 />}
            disabled={downloading}
            onSelect={(e) => {
              e.preventDefault()
              handleDownload()
            }}
          >
            {downloading ? "Preparing PDF…" : "Download as PDF"}
          </DesignMenu.Item>
          {(showDefer || showDeferred) && (
            <>
              <DesignMenu.Separator />
              {showDefer && (
                <DesignMenu.Item
                  icon={<ClockSnooze />}
                  onSelect={() => setDeferDialogOpen(true)}
                >
                  Mark deferred
                </DesignMenu.Item>
              )}
              {showDeferred && (
                <>
                  <DesignMenu.Item
                    icon={<PlayCircle />}
                    disabled={deferralMutation.isPending}
                    onSelect={(e) => {
                      e.preventDefault()
                      handleReactivate()
                    }}
                  >
                    Re-activate
                  </DesignMenu.Item>
                  <DesignMenu.Item
                    destructive
                    icon={<XCircle />}
                    onSelect={() => setConfirmDeadOpen(true)}
                  >
                    Confirm dead
                  </DesignMenu.Item>
                </>
              )}
            </>
          )}
          {canMarkTotalLoss && (
            <>
              <DesignMenu.Separator />
              {!estimate.totalLoss ? (
                <DesignMenu.Item
                  destructive
                  icon={<AlertTriangle />}
                  onSelect={() => setMarkDialogOpen(true)}
                >
                  Mark as total loss
                </DesignMenu.Item>
              ) : (
                <DesignMenu.Item
                  icon={<XClose />}
                  disabled={unmarkMutation.isPending}
                  onSelect={(e) => {
                    e.preventDefault()
                    handleUnmark()
                  }}
                >
                  Unmark total loss
                </DesignMenu.Item>
              )}
            </>
          )}
        </DesignMenu.Content>
      </DesignMenu.Root>

      <MarkTotalLossDialog
        open={markDialogOpen}
        estimateId={estimate.id}
        onClose={() => setMarkDialogOpen(false)}
        mutation={markMutation}
      />
      <DeferDialog
        open={deferDialogOpen}
        estimateId={estimate.id}
        onClose={() => setDeferDialogOpen(false)}
        mutation={deferralMutation}
      />
      <ConfirmDeadDialog
        open={confirmDeadOpen}
        estimateId={estimate.id}
        onClose={() => setConfirmDeadOpen(false)}
        mutation={deferralMutation}
      />
    </>
  )
}

/* ──────────────────────────────────────────────────────────
 * Section header — plain, sentence-case, no eyebrow gymnastics
 * ────────────────────────────────────────────────────────── */

function SectionTitle({ title, meta }: { title: string; meta?: string }) {
  return (
    <div className="mb-3 flex items-baseline justify-between gap-3">
      <h2 className="text-[18px] font-bold text-zinc-900 tracking-tight">{title}</h2>
      {meta && (
        <span className="text-[12px] font-medium text-zinc-400 tabular-nums shrink-0">
          {meta}
        </span>
      )}
    </div>
  )
}

/* ──────────────────────────────────────────────────────────
 * Info grid — Customer / Vehicle / Dealership / Bodyshop
 * ────────────────────────────────────────────────────────── */

function InfoGrid({ estimate }: { estimate: ApiEstimate }) {
  const v = estimate.vehicle
  const c = v?.customer
  const d = estimate.dealership
  const b = estimate.bodyshop

  return (
    <section>
      <SectionTitle title="Customer & vehicle" />
      <div className="grid gap-4 sm:grid-cols-2">
        <InfoCard title="Customer">
          <InfoRow label="Name" value={c?.name ?? estimateCustomerName(estimate)} />
          <InfoRow label="Email" value={estimateCustomerEmail(estimate)} />
          <InfoRow label="Phone" value={estimateCustomerPhone(estimate)} mono />
          {c?.id != null && (
            <Link
              to={`/customers/${c.id}`}
              className="inline-flex items-center gap-1 mt-3 text-[12px] font-semibold text-emerald-700 hover:text-emerald-800 transition-colors"
            >
              {/* Before Confirm there is no follow-up to see — don't promise
                  one on the label (#60). */}
              {estimate.status === "Draft" || estimate.status === "Pending"
                ? "View customer →"
                : "View customer & follow-up →"}
            </Link>
          )}
        </InfoCard>
        <InfoCard title="Vehicle">
          <InfoRow label="VIN" value={v?.vin} mono />
          <InfoRow label="Year" value={v?.year} />
          <InfoRow label="Make" value={v?.make} />
          <InfoRow label="Model" value={v?.model} />
          <InfoRow label="Trim" value={v?.trim} />
          {v?.color && <InfoRow label="Colour" value={v.color} />}
          {v?.type && <InfoRow label="Body" value={humanType(v.type)} />}
        </InfoCard>
        <ConsumerIntakeCard estimate={estimate} />
        {d && (
          <InfoCard title="Dealership">
            <InfoRow label="Name" value={d.name} />
            <InfoRow label="Phone" value={(d as { phoneNumber?: string }).phoneNumber} mono />
            <InfoRow label="Address" value={formatAddress(d as TeamAddress)} />
          </InfoCard>
        )}
        {b && (
          <InfoCard title="Body shop">
            <InfoRow label="Name" value={b.name} />
            <InfoRow label="Phone" value={(b as { phoneNumber?: string }).phoneNumber} mono />
            <InfoRow label="Address" value={formatAddress(b as TeamAddress)} />
          </InfoCard>
        )}
      </div>
    </section>
  )
}

/* ──────────────────────────────────────────────────────────
 * Consumer intake card — provenance of a public-flow lead.
 * Renders only when the estimate carries metadata.intake
 * (readIntakeMeta), observed facts only; empty rows omitted.
 * ────────────────────────────────────────────────────────── */

const INTAKE_CAUSE_LABEL: Record<IntakeCause, string> = {
  rear_ended: "Rear-ended",
  hit_something: "Hit something",
  hit_parked: "Hit while parked",
  side_scrape: "Side scrape",
  hail: "Hail",
  other: "Other",
}
const INTAKE_DRIVABLE_LABEL: Record<IntakeDrivable, string> = {
  yes: "Yes",
  limp: "Drives, but something's off",
  no: "No — can't drive",
}
const INTAKE_INSURANCE_LABEL: Record<IntakeInsuranceMode, string> = {
  insurance: "Through insurance",
  out_of_pocket: "Paying out of pocket",
  unsure: "Not sure yet",
}
const INTAKE_CHANNEL_LABEL: Record<IntakeContactChannel, string> = {
  text: "Text",
  call: "Call",
  email: "Email",
}
const INTAKE_BEST_TIME_LABEL: Record<IntakeBestTime, string> = {
  morning: "mornings",
  afternoon: "afternoons",
  evening: "evenings",
  anytime: "anytime",
}

function ConsumerIntakeCard({ estimate }: { estimate: ApiEstimate }) {
  const intake = readIntakeMeta(estimate)
  if (!intake) return null

  const cause = intake.situation?.cause
    ? INTAKE_CAUSE_LABEL[intake.situation.cause]
    : null
  const drivable = intake.situation?.drivable
    ? INTAKE_DRIVABLE_LABEL[intake.situation.drivable]
    : null
  const insurance = intake.insurance?.mode
    ? [
        INTAKE_INSURANCE_LABEL[intake.insurance.mode],
        intake.insurance.carrier || null,
        intake.insurance.claimNumber ? `claim ${intake.insurance.claimNumber}` : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : null
  const reach = intake.contactPref?.channel
    ? [
        INTAKE_CHANNEL_LABEL[intake.contactPref.channel],
        intake.contactPref.bestTime
          ? INTAKE_BEST_TIME_LABEL[intake.contactPref.bestTime]
          : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : null
  const consent = intake.consent?.acceptedAt
    ? `Accepted ${new Date(intake.consent.acceptedAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })}`
    : null

  return (
    <InfoCard title="Consumer intake">
      {cause && <InfoRow label="Cause" value={cause} />}
      {drivable && <InfoRow label="Drivable" value={drivable} />}
      {insurance && <InfoRow label="Insurance" value={insurance} />}
      {reach && <InfoRow label="Reach by" value={reach} />}
      <InfoRow label="Via" value={SURFACE_LABEL[intake.surface]} />
      {consent && <InfoRow label="Consent" value={consent} />}
    </InfoCard>
  )
}

function InfoCard({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-2xl border border-zinc-200/80 bg-white shadow-sm overflow-hidden">
      <div className="px-5 pt-4 pb-2.5 border-b border-zinc-100">
        <h3 className="text-[13.5px] font-bold text-zinc-900">{title}</h3>
      </div>
      <dl className="divide-y divide-zinc-100">{children}</dl>
    </div>
  )
}

function InfoRow({
  label,
  value,
  mono,
}: {
  label: string
  value: string | null | undefined
  mono?: boolean
}) {
  const display = value && value.toString().trim() !== "" ? value : "—"
  return (
    <div className="px-5 py-2.5 flex items-baseline gap-3">
      <dt className="text-[11.5px] font-semibold uppercase tracking-[0.1em] text-zinc-400 w-20 shrink-0">
        {label}
      </dt>
      <dd
        className={cx(
          "min-w-0 flex-1 text-[13.5px] text-zinc-900 truncate",
          mono && "font-mono tracking-wider",
          display === "—" && "text-zinc-300",
        )}
      >
        {display}
      </dd>
    </div>
  )
}

/* ──────────────────────────────────────────────────────────
 * Photos
 * ────────────────────────────────────────────────────────── */

function PhotosSection({ images, items }: { images: ApiEstimateImage[]; items: ApiEstimateLineItem[] }) {
  const [openIdx, setOpenIdx] = useState<number | null>(null)
  // The components on a photo — the estimate filtered to that photo.
  const onPhoto = (img: ApiEstimateImage) =>
    items.filter((li) => photoLinksOf(li).some((p) => p.imageId === img.id))
  return (
    <section>
      <SectionTitle
        title="Photos"
        meta={`${images.length} ${images.length === 1 ? "photo" : "photos"}`}
      />
      <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
        {images.map((img, i) => (
          <PhotoCard key={img.id} img={img} lines={onPhoto(img)} onOpen={() => setOpenIdx(i)} />
        ))}
      </div>
      <Lightbox
        open={openIdx != null}
        photos={images}
        index={openIdx ?? 0}
        onClose={() => setOpenIdx(null)}
        onIndex={setOpenIdx}
      />
    </section>
  )
}

function PhotoCard({
  img,
  lines,
  onOpen,
}: {
  img: ApiEstimateImage
  lines: ApiEstimateLineItem[]
  onOpen: () => void
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group block overflow-hidden rounded-2xl border border-zinc-200/80 bg-white shadow-sm text-left"
    >
      <div className="relative aspect-[4/3] bg-zinc-100">
        <img
          src={img.src}
          alt={img.description ?? "Estimate photo"}
          className="absolute inset-0 size-full object-cover transition-transform duration-300 group-hover:scale-[1.01]"
          loading="lazy"
        />
      </div>
      <div className="px-4 py-2.5">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-500 truncate">
            {humanSide(img.side)}
          </p>
          <span
            className={cx(
              "text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded-md",
              img.quality >= 80
                ? "bg-emerald-50 text-emerald-700"
                : img.quality >= 60
                  ? "bg-amber-50 text-amber-700"
                  : "bg-zinc-100 text-zinc-600",
            )}
          >
            {img.quality}%
          </span>
        </div>
        {lines.length > 0 && (
          <p className="mt-1 text-[12.5px] text-zinc-600">
            {lines.map((li) => componentLabel(li.component)).join(" · ")}
          </p>
        )}
      </div>
    </button>
  )
}

function Lightbox({
  open,
  photos,
  index,
  onClose,
  onIndex,
}: {
  open: boolean
  photos: ApiEstimateImage[]
  index: number
  onClose: () => void
  onIndex: (i: number | null) => void
}) {
  const photo = photos[index]
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
      if (e.key === "ArrowRight")
        onIndex(Math.min(photos.length - 1, index + 1))
      if (e.key === "ArrowLeft") onIndex(Math.max(0, index - 1))
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, index, photos.length, onClose, onIndex])

  return (
    <AnimatePresence>
      {open && photo && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            className="relative max-w-5xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={photo.src}
              alt={photo.description ?? "Estimate photo"}
              className="w-full max-h-[80vh] object-contain rounded-2xl"
            />
            <button
              type="button"
              onClick={onClose}
              className="absolute top-3 right-3 size-9 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm grid place-items-center transition-colors"
              aria-label="Close"
            >
              <XClose className="size-4" />
            </button>
            <div className="absolute inset-x-0 bottom-3 flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => onIndex(Math.max(0, index - 1))}
                disabled={index === 0}
                className="size-9 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm grid place-items-center disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                aria-label="Previous"
              >
                <ChevronLeft className="size-4" />
              </button>
              <span className="rounded-full bg-white/10 backdrop-blur-sm px-3 py-1.5 text-[12px] font-medium text-white">
                <span className="capitalize">{humanSide(photo.side)}</span>
                <span className="text-white/50 mx-2">·</span>
                <span className="tabular-nums">{photo.quality}% quality</span>
                <span className="text-white/50 mx-2">·</span>
                <span className="tabular-nums">
                  {index + 1} / {photos.length}
                </span>
              </span>
              <button
                type="button"
                onClick={() =>
                  onIndex(Math.min(photos.length - 1, index + 1))
                }
                disabled={index === photos.length - 1}
                className="size-9 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm grid place-items-center disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                aria-label="Next"
              >
                <ChevronRight className="size-4" />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

/* ──────────────────────────────────────────────────────────
 * Damage — real table
 * ────────────────────────────────────────────────────────── */

function DamageSection({ items }: { items: ApiEstimateLineItem[] }) {
  if (items.length === 0) {
    return (
      <section>
        <SectionTitle title="Damage" />
        <EmptyCard>
          When AI finishes analysing the photos, the damage line items appear
          here.
        </EmptyCard>
      </section>
    )
  }

  const sorted = [...items].sort((a, b) => {
    const sa = SECTION_ORDER.indexOf(a.section as VehicleSection)
    const sb = SECTION_ORDER.indexOf(b.section as VehicleSection)
    return (sa < 0 ? 99 : sa) - (sb < 0 ? 99 : sb)
  })

  return (
    <section>
      <SectionTitle
        title="Damage"
        meta={`${items.length} ${items.length === 1 ? "item" : "items"}`}
      />
      <div className="rounded-2xl border border-zinc-200/80 bg-white shadow-sm overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-zinc-50/70 border-b border-zinc-200">
              <Th className="w-[22%]">Section</Th>
              <Th className="w-[26%]">Component</Th>
              <Th className="w-[12%]">Action</Th>
              <Th>Damage description</Th>
              <Th className="text-right w-[14%]">Cost</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {sorted.map((li) => (
              <DamageRow key={li.id} li={li} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function Th({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <th
      className={cx(
        "px-4 py-2.5 text-left text-[10.5px] font-bold uppercase tracking-[0.12em] text-zinc-500",
        className,
      )}
    >
      {children}
    </th>
  )
}

function Td({
  children,
  className,
  align,
}: {
  children: React.ReactNode
  className?: string
  align?: "right"
}) {
  return (
    <td
      className={cx(
        "px-4 py-3 text-[13px] text-zinc-800 align-top",
        align === "right" && "text-right",
        className,
      )}
    >
      {children}
    </td>
  )
}

function DamageRow({ li }: { li: ApiEstimateLineItem }) {
  return (
    <tr className="hover:bg-zinc-50/50 transition-colors">
      <Td>
        <p className="font-semibold text-zinc-900">{humanSection(li.section)}</p>
        {li.side && (
          <p className="mt-0.5 text-[11.5px] text-zinc-400 capitalize">
            {humanSide(li.side)}
          </p>
        )}
      </Td>
      <Td>
        <p className="font-medium text-zinc-800">{humanComponent(li.component)}</p>
      </Td>
      <Td>
        <ActionChip action={li.action} partType={li.partType} />
      </Td>
      <Td>
        {li.description ? (
          <p className="text-[13px] text-zinc-700 leading-relaxed">{li.description}</p>
        ) : (
          <span className="text-zinc-300">—</span>
        )}
      </Td>
      <Td align="right">
        <p className="font-semibold tabular-nums text-zinc-900">
          {fmtUSD(li.totalCost)}
        </p>
        {li.manHours > 0 && (
          <p className="mt-0.5 text-[11.5px] text-zinc-400 tabular-nums">
            {li.manHours.toFixed(1)}h
          </p>
        )}
      </Td>
    </tr>
  )
}

function ActionChip({
  action,
  partType,
}: {
  action: string | null
  partType: string | null
}) {
  if (!action) return null
  const isReplace = action.toLowerCase() === "replace"
  const showPart = isReplace && !!partType
  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      <Chip>{isReplace ? "Replace" : "Repair"}</Chip>
      {showPart && <Chip>{partType}</Chip>}
    </span>
  )
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-md border border-zinc-200 bg-white px-1.5 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.04em] text-zinc-700">
      {children}
    </span>
  )
}

/* ──────────────────────────────────────────────────────────
 * Operations — Section → Component → Ops (3-level).
 *
 * Real BE returns operations as children of each line item;
 * lineItem.component anchors the middle level. We surface
 * Section as a divider row, Component as a sub-header row,
 * and ops as the leaf rows.
 * ────────────────────────────────────────────────────────── */

const WORK_TYPE_LABEL: Record<SectionWorkType, string> = {
  body_repair: "Body Repair",
  refinish: "Refinish",
  refinish_prep: "Refinish Prep",
  refinish_adder: "Refinish Adder",
  adas_diagnostic: "ADAS Diagnostic",
  mechanical: "Mechanical",
  shop_admin: "Shop Admin",
  sublet_other: "Sublet · Other",
}

interface ComponentGroup {
  component: string
  side: string | null
  ops: ApiOperation[]
  totalHours: number
}

interface SectionGroup {
  section: string
  components: ComponentGroup[]
  totalHours: number
}

function groupOperations(lineItems: ApiEstimateLineItem[]): SectionGroup[] {
  const map = new Map<string, ComponentGroup[]>()
  for (const li of lineItems) {
    const ops = li.operations ?? []
    if (ops.length === 0) continue
    const totalHours = ops.reduce((s, o) => s + (o.laborHours ?? 0), 0)
    const list = map.get(li.section) ?? []
    list.push({
      component: li.component,
      side: li.side,
      ops,
      totalHours,
    })
    map.set(li.section, list)
  }
  return [...map.entries()]
    .sort(([a], [b]) => {
      const ai = SECTION_ORDER.indexOf(a as VehicleSection)
      const bi = SECTION_ORDER.indexOf(b as VehicleSection)
      return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi)
    })
    .map(([section, components]) => ({
      section,
      components,
      totalHours: components.reduce((s, c) => s + c.totalHours, 0),
    }))
}

function OperationsSection({
  lineItems,
  totalHours,
  totalOps,
}: {
  lineItems: ApiEstimateLineItem[]
  totalHours: number
  totalOps: number
}) {
  const groups = useMemo(() => groupOperations(lineItems), [lineItems])

  return (
    <section>
      <SectionTitle
        title="Operations"
        meta={`${totalOps} · ${totalHours.toFixed(1)} labour hours`}
      />
      <div className="rounded-2xl border border-zinc-200/80 bg-white shadow-sm overflow-hidden">
        <table className="w-full">
          <colgroup>
            <col className="w-[55%]" />
            <col className="w-[22%]" />
            <col />
          </colgroup>
          <thead>
            <tr className="bg-zinc-50/70 border-b border-zinc-200">
              <Th>Description</Th>
              <Th>Work type</Th>
              <Th className="text-right">Hours</Th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g, gi) => (
              <OpsSectionRows key={g.section} group={g} divider={gi > 0} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function OpsSectionRows({
  group,
  divider,
}: {
  group: SectionGroup
  divider: boolean
}) {
  return (
    <>
      <tr
        className={cx(
          "bg-zinc-50/40",
          divider && "border-t border-zinc-200",
        )}
      >
        <td colSpan={2} className="px-4 py-2">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-500">
            <span className="text-zinc-400">Section ·</span>{" "}
            <span className="text-zinc-900">{humanSection(group.section)}</span>
          </p>
        </td>
        <td className="px-4 py-2 text-right">
          <span className="text-[11px] font-semibold tabular-nums text-zinc-500">
            {group.totalHours.toFixed(1)}h
          </span>
        </td>
      </tr>
      {group.components.map((c) => (
        <ComponentRows
          key={`${group.section}-${c.component}-${c.side ?? ""}`}
          comp={c}
        />
      ))}
    </>
  )
}

function ComponentRows({ comp }: { comp: ComponentGroup }) {
  return (
    <>
      <tr className="border-t border-zinc-100">
        <td colSpan={3} className="px-4 pt-3 pb-1">
          <span className="text-[12.5px] font-semibold text-zinc-900">
            {humanComponent(comp.component)}
          </span>
          {comp.side && (
            <span className="ml-2 text-[10.5px] uppercase tracking-[0.04em] text-zinc-400">
              {humanSide(comp.side)}
            </span>
          )}
        </td>
      </tr>
      {comp.ops.map((op, i) => (
        <tr
          key={op.id ?? `${comp.component}-${i}`}
          className="border-t border-zinc-100 hover:bg-zinc-50/50 transition-colors"
        >
          <Td>
            <p className="text-[13px] text-zinc-800 pl-3">{op.description}</p>
          </Td>
          <Td>
            <span className="text-[12px] text-zinc-600">
              {WORK_TYPE_LABEL[op.workType] ?? op.workType}
            </span>
          </Td>
          <Td align="right">
            <span className="font-semibold tabular-nums text-zinc-800">
              {op.laborHours.toFixed(1)}h
            </span>
          </Td>
        </tr>
      ))}
    </>
  )
}

/* ──────────────────────────────────────────────────────────
 * Notes
 * ────────────────────────────────────────────────────────── */

// The estimate's history: what a person did (answered tasks, full weight) and
// what the automation did (collapsed to one line). A task with its answer IS
// the log — there is no separate notes feature to fall out of sync with it.
function FollowUpTimeline({ estimate }: { estimate: ApiEstimate }) {
  const { user } = useAuth()
  const meId = user?.id != null ? Number(user.id) : null
  const { data } = useEstimateTasks(estimate.id)
  const [showAuto, setShowAuto] = useState(false)
  const activity = useCustomerActivity(estimate.vehicle?.customer?.id ?? null)

  const answered = (data?.tasks ?? []).filter((t) => t.status === "done" && t.answer)
  const open = (data?.tasks ?? []).filter((t) => t.status === "open")
  const autoEvents = (activity.data?.events ?? []).filter((e) =>
    ["sms_sent", "email_sent", "opened", "replied", "delivered"].includes(e.kind),
  )
  const opened = autoEvents.filter((e) => e.kind === "opened").length
  const sent = autoEvents.filter((e) => e.kind === "sms_sent" || e.kind === "email_sent").length

  return (
    <section>
      <SectionTitle title="History" />
      <div className="rounded-2xl border border-zinc-200/80 bg-white shadow-sm px-6 py-5">
        {estimate.notes && (
          <div className="pb-4 mb-4 border-b border-zinc-100">
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-400 mb-1.5">
              Written up as
            </p>
            <p className="text-[14px] text-zinc-700 whitespace-pre-wrap leading-relaxed">
              {estimate.notes}
            </p>
          </div>
        )}

        {open.length > 0 && (
          <p className="text-[12.5px] text-teal-700 font-semibold mb-3">
            Open: {describeOpen(open[0])}
            <Link to={`/tasks?estimate=${estimate.id}`} className="ml-2 text-zinc-500 hover:text-zinc-900 font-semibold">
              Handle it →
            </Link>
          </p>
        )}

        {answered.length === 0 && sent === 0 && (
          <p className="text-[13px] text-zinc-500">Nothing yet. Calls answered on the Tasks screen land here.</p>
        )}

        <ol className="space-y-3">
          {answered.map((t) => (
            <li key={t.id} className="flex items-start gap-2.5">
              <span className="mt-1.5 size-1.5 rounded-full bg-zinc-900 shrink-0" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="text-[13.5px] text-zinc-900 leading-snug">{describeTask(t, meId)}</p>
                {t.note && <p className="text-[12.5px] text-zinc-500 italic mt-0.5">"{t.note}"</p>}
                <p className="text-[11.5px] text-zinc-400 mt-0.5 tabular-nums">
                  {new Date(t.answeredAt!).toLocaleString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit" })}
                </p>
              </div>
            </li>
          ))}

          {sent > 0 && (
            <li className="flex items-start gap-2.5">
              <span className="mt-1.5 size-1.5 rounded-full bg-zinc-200 shrink-0" aria-hidden />
              <div className="min-w-0 flex-1">
                <button type="button" onClick={() => setShowAuto((v) => !v)} className="text-[13px] text-zinc-500 hover:text-zinc-900 transition-colors">
                  {sent} automated {sent === 1 ? "message" : "messages"}
                  {opened > 0 && ` · ${opened} opened`}
                  <span className="text-zinc-400 ml-1.5">{showAuto ? "hide" : "show"}</span>
                </button>
                {showAuto && (
                  <ol className="mt-2 space-y-1.5">
                    {autoEvents.map((a) => (
                      <li key={a.id} className="text-[12.5px] text-zinc-500">
                        {a.stepLabel ?? a.kind.replace(/_/g, " ")}
                        <span className="text-zinc-300 mx-1.5">·</span>
                        <span className="tabular-nums">
                          {new Date(a.occurredAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        </span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </li>
          )}
        </ol>
      </div>
    </section>
  )
}

function describeOpen(t: TaskDoc): string {
  switch (t.type) {
    case "review": return "review the estimate"
    case "reach_out": return "first call"
    case "call": return `call · ${attemptLabel(t.attempt)}`
    case "callback": return "they asked for a call"
    case "fix_contact": return "get a working number"
    case "close_out": return "wrap it up — close, or call again"
  }
}

// "You" when it was this user, the colleague's name when it wasn't — the thing
// worth knowing before dialling is whether somebody already had this talk.
function describeTask(t: TaskDoc, meId: number | null): string {
  const who = t.answeredBy?.id === meId ? "You" : (t.answeredBy?.name ?? "Someone")
  const a = t.answer!
  const tries = t.attempt > 1 ? ` · ${attemptLabel(t.attempt)}` : ""
  switch (a.kind) {
    case "confirmed": return `${who} confirmed the estimate`
    case "reached": {
      const then =
        a.then === "booked" ? " — booked a visit"
        : a.then === "callback" ? " — call back requested"
        : a.then === "declined" ? ` — passed (${DECLINE_LABEL[a.declineReason ?? "other"].toLowerCase()})`
        : a.then === "sent_estimate" ? " — sent the estimate"
        : a.then === "needs_changes" ? " — estimate needs changes"
        : " — still thinking"
      return `${who} called — ${OUTCOME_LABEL.reached}${then}${tries}`
    }
    case "no_answer":
    case "voicemail":
    case "bad_number":
      return `${who} called — ${OUTCOME_LABEL[a.kind]}${tries}`
    case "contact_fixed": return `${who} fixed the number`
    case "closed": return `${who} closed it — ${DECLINE_LABEL[a.declineReason ?? "unreachable"].toLowerCase()}`
    case "keep_trying":
      return a.callbackAt
        ? `${who} chose to call again ${new Date(a.callbackAt).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}`
        : `${who} chose to try once more`
    case "parked": return `${who} parked it`
  }
}

/* ──────────────────────────────────────────────────────────
 * Total — bottom band, document-style
 * ────────────────────────────────────────────────────────── */

function TotalBand({
  estimate,
  totalHours,
}: {
  estimate: ApiEstimate
  totalHours: number
}) {
  const range = `${fmtUSD(estimate.min)} – ${fmtUSD(estimate.max)}`
  return (
    <section>
      <div className="rounded-2xl border border-zinc-200/80 bg-white shadow-sm px-6 md:px-8 py-6 md:py-7">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-zinc-500">
              Total estimated
            </p>
            <p className="mt-1 text-[34px] md:text-[42px] font-bold text-zinc-900 tracking-tight tabular-nums leading-none">
              {range}
            </p>
            <p className="mt-2 text-[12.5px] text-zinc-500 tabular-nums">
              Avg {fmtUSD(estimate.avg)}
              {totalHours > 0 ? ` · ${totalHours.toFixed(1)} labour hours` : ""}
              {(estimate.lineItems?.length ?? 0) > 0
                ? ` · ${estimate.lineItems!.length} line ${estimate.lineItems!.length === 1 ? "item" : "items"}`
                : ""}
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ──────────────────────────────────────────────────────────
 * Mark-as-total-loss dialog
 * ────────────────────────────────────────────────────────── */

interface MarkDialogProps {
  open: boolean
  estimateId: number
  onClose: () => void
  mutation: ReturnType<typeof useMarkAsTotalLoss>
}

function MarkTotalLossDialog({
  open,
  estimateId,
  onClose,
  mutation,
}: MarkDialogProps) {
  const [department, setDepartment] = useState<SalesLeadDepartment>("new")

  const handleConfirm = () => {
    mutation.mutate(
      { estimateId, data: { department } },
      {
        onSuccess: () => {
          toast.success("Marked as total loss", {
            description: "A sales lead has been created for the dealership.",
          })
          onClose()
        },
        onError: (err) =>
          toast.error(
            err instanceof Error ? err.message : "Couldn't mark as total loss",
          ),
      },
    )
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", stiffness: 350, damping: 30 }}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] w-full max-w-md"
          >
            <div className="rounded-2xl border border-zinc-200/60 bg-white shadow-xl p-6">
              <div className="flex items-start gap-3 mb-5">
                <div className="size-10 rounded-xl bg-red-50 flex items-center justify-center shrink-0">
                  <AlertTriangle className="size-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-[18px] font-bold text-zinc-900 tracking-tight">
                    Mark as total loss
                  </h3>
                  <p className="mt-1 text-[13px] text-zinc-500 leading-relaxed">
                    The vehicle will be flagged as a total loss and a follow-up
                    sales lead will be created on the dealership side.
                  </p>
                </div>
              </div>

              <div className="mb-5">
                <label className="text-[12px] font-semibold uppercase tracking-[0.1em] text-zinc-500 mb-2 block">
                  Sales department
                </label>
                <select
                  value={department}
                  onChange={(e) =>
                    setDepartment(e.target.value as SalesLeadDepartment)
                  }
                  className="w-full h-10 rounded-xl border border-zinc-200 bg-zinc-50 px-4 text-[13.5px] text-zinc-900 outline-none focus:border-zinc-400 focus:bg-white transition-all duration-150"
                >
                  <option value="new">New vehicle</option>
                  <option value="used">Used vehicle</option>
                </select>
                <p className="text-[11.5px] text-zinc-400 mt-1.5">
                  The dealership's sales team for this department will pick up
                  the lead.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  disabled={mutation.isPending}
                  className="flex-1 h-10 rounded-xl border border-zinc-200 text-[13.5px] font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-40"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={mutation.isPending}
                  className="flex-1 h-10 rounded-xl bg-red-600 text-white text-[13.5px] font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {mutation.isPending ? "Marking…" : "Mark as total loss"}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

/* ──────────────────────────────────────────────────────────
 * Deferral dialogs — defer (optional note) + confirm-dead
 * ────────────────────────────────────────────────────────── */

interface DeferralDialogProps {
  open: boolean
  estimateId: number
  onClose: () => void
  mutation: ReturnType<typeof useSetDeferral>
}

function DeferDialog({ open, estimateId, onClose, mutation }: DeferralDialogProps) {
  const [note, setNote] = useState("")

  const handleConfirm = () => {
    mutation.mutate(
      { estimateId, action: "defer", note: note.trim() || undefined },
      {
        onSuccess: () => {
          toast.success("Estimate marked deferred", {
            description: "It stays a live opportunity until re-activated or confirmed dead.",
          })
          setNote("")
          onClose()
        },
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : "Couldn't mark deferred"),
      },
    )
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", stiffness: 350, damping: 30 }}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] w-full max-w-md"
          >
            <div className="rounded-2xl border border-zinc-200/60 bg-white shadow-xl p-6">
              <div className="flex items-start gap-3 mb-5">
                <div className="size-10 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                  <ClockSnooze className="size-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-[18px] font-bold text-zinc-900 tracking-tight">
                    Mark deferred
                  </h3>
                  <p className="mt-1 text-[13px] text-zinc-500 leading-relaxed">
                    Parks this estimate out of follow-up pressure without losing
                    it — the status stays as-is and it can be re-activated later.
                  </p>
                </div>
              </div>

              <div className="mb-5">
                <label className="text-[12px] font-semibold uppercase tracking-[0.1em] text-zinc-500 mb-2 block">
                  Note (optional)
                </label>
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. Customer waiting on insurance"
                  maxLength={120}
                  className="w-full h-10 rounded-xl border border-zinc-200 bg-zinc-50 px-4 text-[13.5px] text-zinc-900 placeholder:text-zinc-400 outline-none focus:border-zinc-400 focus:bg-white transition-all duration-150"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  disabled={mutation.isPending}
                  className="flex-1 h-10 rounded-xl border border-zinc-200 text-[13.5px] font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-40"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={mutation.isPending}
                  className="flex-1 h-10 rounded-xl bg-amber-600 text-white text-[13.5px] font-semibold hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {mutation.isPending ? "Deferring…" : "Mark deferred"}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

function ConfirmDeadDialog({ open, estimateId, onClose, mutation }: DeferralDialogProps) {
  const handleConfirm = () => {
    mutation.mutate(
      { estimateId, action: "confirm_dead" },
      {
        onSuccess: () => {
          toast.success("Opportunity confirmed dead", {
            description: "The estimate has been declined.",
          })
          onClose()
        },
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : "Couldn't confirm dead"),
      },
    )
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", stiffness: 350, damping: 30 }}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] w-full max-w-md"
          >
            <div className="rounded-2xl border border-zinc-200/60 bg-white shadow-xl p-6">
              <div className="flex items-start gap-3 mb-5">
                <div className="size-10 rounded-xl bg-red-50 flex items-center justify-center shrink-0">
                  <XCircle className="size-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-[18px] font-bold text-zinc-900 tracking-tight">
                    Confirm opportunity dead?
                  </h3>
                  <p className="mt-1 text-[13px] text-zinc-500 leading-relaxed">
                    Clears the deferral and declines the estimate. It leaves the
                    live pipeline but remains in the system for record keeping.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  disabled={mutation.isPending}
                  className="flex-1 h-10 rounded-xl border border-zinc-200 text-[13.5px] font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-40"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={mutation.isPending}
                  className="flex-1 h-10 rounded-xl bg-red-600 text-white text-[13.5px] font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {mutation.isPending ? "Confirming…" : "Confirm dead"}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

/* ──────────────────────────────────────────────────────────
 * Empty / loading / 404
 * ────────────────────────────────────────────────────────── */

function EmptyCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-dashed border-zinc-200 bg-white/40 p-5 text-center text-[13px] text-zinc-500">
      {children}
    </div>
  )
}

function Skeleton() {
  return (
    <>
      <div className="h-4 w-24 rounded bg-zinc-100 mb-4" />
      <div className="rounded-2xl bg-white border border-zinc-200/70 h-32 animate-pulse" />
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="rounded-2xl bg-white border border-zinc-200/70 h-44 animate-pulse"
          />
        ))}
      </div>
    </>
  )
}

function NotFound({ onBack }: { onBack: () => void }) {
  return (
    <div className="py-24 text-center">
      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-600">
        404
      </p>
      <h1 className="mt-2 text-[22px] font-bold text-zinc-900">
        Estimate not found.
      </h1>
      <p className="mt-1 text-[13px] text-zinc-500">
        It may have been deleted or moved to another team.
      </p>
      <div className="mt-6 inline-block">
        <PrimaryButton onClick={onBack} variant="dark">
          Back to estimates
        </PrimaryButton>
      </div>
    </div>
  )
}

/* ──────────────────────────────────────────────────────────
 * Helpers — section ordering + formatting + label maps
 * ────────────────────────────────────────────────────────── */

const SECTION_ORDER: VehicleSection[] = [
  "FRONT_BUMPER_GRILLE",
  "HOOD",
  "FRONT_LAMPS",
  "FENDER",
  "FRONT_DOOR",
  "REAR_DOOR",
  "QUARTER_PANEL",
  "ROCKER_PANEL",
  "ROOF",
  "WINDSHIELD",
  "REAR_LAMPS",
  "TRUNK_LID",
  "TRUNK",
  "LIFT_GATE",
  "TAIL_GATE",
  "REAR_BUMPER",
  "WHEELS",
  "FRAME",
  "PICKUP_BOX",
]

interface TeamAddress {
  street?: string | null
  city?: string | null
  state?: string | null
  zipCode?: string | null
  country?: string | null
}

function humanSection(s: string): string {
  const lower = s.replace(/_/g, " ").toLowerCase()
  return lower.charAt(0).toUpperCase() + lower.slice(1)
}

function humanComponent(c: string): string {
  return humanSection(c)
}

function humanSide(s: string): string {
  return humanSection(s)
}

function humanType(t: string): string {
  return t.replace(/([a-z])([A-Z])/g, "$1 $2")
}

function fmtUSD(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  })
}

function formatAddress(team: TeamAddress): string | null {
  const parts = [team.street, team.city, team.state, team.zipCode, team.country].filter(
    Boolean,
  )
  return parts.length > 0 ? (parts.join(", ") as string) : null
}

/* ─────────────────────────────────────────────────────────────────── */

export default function EstimateDetailPage() {
  return (
    <ProtectedRoute allowedRoles={ALL_ROLES}>
      <AppLayout>
        <EstimateDetailPageInner />
      </AppLayout>
    </ProtectedRoute>
  )
}
